import { useState } from "react";
import { Clock } from "lucide-react";
import { AvatarUpload, Button, Card, CardContent, CardHeader, CardTitle, Input, Label, userInitials } from "@wa/ui";
import { useT, useI18n } from "../../i18n/I18nProvider";
import { api, ApiError } from "../../lib/api";
import { requestEmailChange, type AccountUser } from "../../lib/auth";
import { useShell } from "../shell/context";

export function ProfileSection({ user, onUserChange }: { user: AccountUser; onUserChange: (u: AccountUser) => void }) {
  const t = useT();
  const { variant } = useShell();
  const { locale } = useI18n();
  const [name, setName] = useState(user.name);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  async function save() {
    if (!name.trim()) return;
    setBusy(true);
    setSaved(false);
    try {
      await api.patch("/me/profile", { name: name.trim() });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } finally {
      setBusy(false);
    }
  }

  // ── Email change ───────────────────────────────────────────────────────────
  const [editEmail, setEditEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailErr, setEmailErr] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  function openEmail() {
    setNewEmail("");
    setPwd("");
    setEmailErr(null);
    setPendingEmail(null);
    setEditEmail(true);
  }

  async function submitEmail() {
    setEmailErr(null);
    if (!newEmail.trim()) return;
    setEmailBusy(true);
    try {
      const res = await requestEmailChange(newEmail.trim(), user.hasPassword ? pwd : undefined, locale);
      setPendingEmail(res.pendingEmail);
      setEditEmail(false);
    } catch (e) {
      const code = e instanceof ApiError ? String((e.body as { error?: string })?.error ?? "") : "";
      const known = ["email_taken", "wrong_password", "password_required", "same_email"];
      setEmailErr(t(`profile.changeEmail.errors.${known.includes(code) ? code : "generic"}`));
    } finally {
      setEmailBusy(false);
    }
  }

  return (
    <Card>
      {/* On the portal's account page the section heading above already
          says this word, so the card does not say it twice. */}
      {variant !== "portal" && (
        <CardHeader>
          <CardTitle>{t("profile.title")}</CardTitle>
        </CardHeader>
      )}
      <CardContent className="space-y-4 max-w-md">
        {/* Photo upload is a SHARED component (@wa/ui AvatarUpload) - the same
            /me/avatar endpoint also serves web_admin's sidebar menu. The response
            is the full /me payload → onUserChange refreshes the whole hub (welcome card). */}
        {/* More air than the text fields get: a 64px circle right under a
            small label reads as if the label sits on top of it. A grid gap,
            not `space-y`: the label is inline, so a margin under it does
            nothing. */}
        <div className="grid gap-3">
          <Label htmlFor="avatar-file">{t("profile.photo.label")}</Label>
          <AvatarUpload
            inputId="avatar-file"
            src={user.avatarUrl}
            fallback={userInitials(user.name, user.email)}
            fallbackClassName="bg-primary/15 text-primary font-bold"
            labels={{
              change: t("profile.photo.change"),
              remove: t("profile.photo.remove"),
              errorGeneric: t("profile.photo.errors.generic"),
              errorTooLarge: t("profile.photo.errors.too_large"),
              errorType: t("profile.photo.errors.type"),
            }}
            onUpload={async (file) => onUserChange(await api.upload<AccountUser>("/me/avatar", file))}
            onRemove={async () => onUserChange(await api.del<AccountUser>("/me/avatar"))}
          />
          <p className="text-xs text-muted-foreground">{t("profile.photo.hint")}</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="name">{t("profile.name")}</Label>
          <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="email">{t("profile.email")}</Label>
          <Input id="email" value={user.email} disabled />
          {pendingEmail ? (
            <p className="text-xs text-primary">{t("profile.changeEmail.pending", { email: pendingEmail })}</p>
          ) : !editEmail ? (
            <button type="button" onClick={openEmail} className="text-xs font-medium text-primary hover:underline">
              {t("profile.changeEmail.cta")}
            </button>
          ) : null}
        </div>

        {editEmail && (
          <div className="space-y-3 rounded-lg border border-border p-3">
            <p className="text-xs text-muted-foreground">{t("profile.changeEmail.hint")}</p>
            <div className="space-y-1.5">
              <Label htmlFor="new-email">{t("profile.changeEmail.newEmail")}</Label>
              <Input id="new-email" type="email" autoComplete="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
            </div>
            {user.hasPassword && (
              <div className="space-y-1.5">
                <Label htmlFor="cur-pwd">{t("profile.changeEmail.currentPassword")}</Label>
                <Input id="cur-pwd" type="password" autoComplete="current-password" value={pwd} onChange={(e) => setPwd(e.target.value)} />
              </div>
            )}
            {emailErr && <p className="text-sm text-destructive">{emailErr}</p>}
            <div className="flex gap-2">
              <Button
                onClick={submitEmail}
                loading={emailBusy}
                disabled={!newEmail.trim() || (user.hasPassword && !pwd)}
              >
                {t("profile.changeEmail.submit")}
              </Button>
              <Button variant="ghost" onClick={() => setEditEmail(false)} disabled={emailBusy}>
                {t("common.cancel")}
              </Button>
            </div>
          </div>
        )}

        <Button onClick={save} loading={busy} disabled={!name.trim() || name.trim() === user.name}>
          {saved ? t("common.saved") : t("profile.save")}
        </Button>

        {/* Last sign-in - security info (buildMe: the most recent login audit event). */}
        {user.lastLoginAt && (
          <p className="flex items-center gap-1.5 border-t border-border pt-3 text-xs text-muted-foreground">
            <Clock className="size-3.5" />
            {t("profile.lastLogin", {
              date: new Date(user.lastLoginAt).toLocaleString(locale === "en" ? "en-GB" : "et-EE", {
                dateStyle: "medium",
                timeStyle: "short",
              }),
            })}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
